import { restApi } from '@icp/settings';
import { immutableDelete, immutableInsert, immutableSet } from '@icp/utils';

// 以前 favoriteView 是存到 localstorage 的，现在改为存到远程数据库了，需要做 migrate
export function migrateLocalToRemote(key, url) {
  if (!key || !url) {
    return;
  }

  const localSettings = loadSetting(key);
  if (!Object.keys(localSettings).length || !localSettings.favoriteView) {
    return;
  }

  const { favoriteView, ...other } = localSettings;

  // favoriteView 存储到远程服务器，其余状态保留到浏览器
  restApi
    .post(`${url}/create-or-update`, {
      key,
      value: JSON.stringify({ favoriteView: { viewList: favoriteView.viewList } }),
      scope: 'user',
    })
    .then(() => {
      localStorage.setItem(
        key,
        // 只留 current 在本地，viewList 存远程
        JSON.stringify({ ...other, favoriteView: { current: favoriteView.current } }),
      );
    });
}

export function saveSetting(key, obj) {
  if (!key) return;
  const oldSettings = loadSetting(key);
  const newSettings = { ...oldSettings, ...obj };
  if (oldSettings.favoriteView) {
    newSettings.favoriteView = oldSettings.favoriteView;
  }
  localStorage.setItem(key, JSON.stringify(newSettings));
}

export function loadSetting(key) {
  if (!key) return {};
  return JSON.parse(localStorage.getItem(key)) || {};
}

export function clearSetting(key) {
  if (!key) return;
  const { favoriteView } = loadSetting(key);
  if (favoriteView) {
    localStorage.setItem(key, JSON.stringify({ favoriteView }));
  } else {
    localStorage.removeItem(key);
  }
}

export async function loadFavoriteView(key, globalKey, url) {
  const local = loadSetting(key).favoriteView;
  const remote = url
    ? await Promise.all([restApi.get(`${url}/key/${key}`), restApi.get(`${url}/key/${globalKey}`)])
        .then((res) => {
          return [
            res[0] ? JSON.parse(res[0].value) : null,
            res[1] ? JSON.parse(res[1].value) : null,
            //
          ];
        })
        .then((res) => {
          return [res[0]?.favoriteView || {}, res[1]?.favoriteView || {}];
        })
        .then(([userRes, globalRes]) => {
          return {
            current: local?.current,
            viewList: [...(globalRes.viewList || []), ...(userRes.viewList || [])],
          };
        })
    : null;

  if (url && local?.viewList?.length && !remote?.viewList?.length) {
    migrateLocalToRemote(key, url);
  }

  if (url) {
    return remote;
  }

  return local;
}

function isSettingEmpty(settings) {
  if (!Object.keys(settings).length) {
    return true;
  }

  const { favoriteView } = settings;
  return (
    Object.keys(favoriteView.length <= 1) &&
    Object.hasOwnProperty.call(favoriteView, 'current') &&
    !favoriteView.current
  );
}

export function saveFavoriteView(key, globalKey, url, newFavoriteView, oldFavoriteView, type) {
  // 不管修改什么，都需要先更新 localstorage 的 current
  const oldSettings = loadSetting(key);
  const newLocalSettings = {
    ...oldSettings,
    favoriteView: {
      ...oldSettings.favoriteView,
      current: newFavoriteView.current,
    },
  };
  if (isSettingEmpty(newLocalSettings)) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify(newLocalSettings));
  }

  if (type === 'switchCurrent') {
    return Promise.resolve(newFavoriteView);
  }

  if (url) {
    const userViewList = newFavoriteView.viewList.filter((item) => !item.isGlobal);
    const oldUserViewList = oldFavoriteView.viewList.filter((item) => !item.isGlobal);
    const globalViewList = newFavoriteView.viewList.filter((item) => item.isGlobal);
    const oldGlobalViewList = oldFavoriteView.viewList.filter((item) => item.isGlobal);

    // update user favorite view
    let promise1;
    if (oldUserViewList.length && !userViewList.length) {
      promise1 = restApi.delete(`${url}/key/${key}`);
    } else if (userViewList.length) {
      promise1 = restApi.post(`${url}/create-or-update`, {
        key,
        value: JSON.stringify({
          favoriteView: { viewList: userViewList },
        }),
        scope: 'user',
      });
    }

    // update global favorite view
    let promise2;
    if (oldGlobalViewList.length && !globalViewList.length) {
      promise2 = restApi.delete(`${url}/key/${globalKey}`);
    } else if (globalViewList.length) {
      promise2 = restApi.post(`${url}/create-or-update`, {
        key: globalKey,
        value: JSON.stringify({
          favoriteView: { viewList: globalViewList },
        }),
        scope: 'global',
      });
    }

    return Promise.all([promise1, promise2]).then(() => newFavoriteView);
  }

  // const oldSettings = loadSetting(key);
  const newSettings = { ...oldSettings, favoriteView: newFavoriteView };
  localStorage.setItem(key, JSON.stringify(newSettings));
  return Promise.resolve(newFavoriteView);
}

export async function updateFavoriteView(config, type, params) {
  const { key, globalKey, url } = config;

  // 为了解决两个用户同时在更新有 global view 的情况，先 fetch 再更新
  const oldFavoriteView = (await loadFavoriteView(key, globalKey, url)) || {
    current: null,
    viewList: [],
  };
  let newFavoriteView = oldFavoriteView;

  const { current, viewList } = oldFavoriteView;

  if (type === 'add') {
    const { newItem } = params;

    // 简单处理，如果有 name 相同的，直接覆盖。因为 FavoriteView.js 里已经检查过 name 不重复了，所以这种情况只会
    // 发生在远程同时有另一个窗口在对 favoriteView 做修改，概率很小，不做警告处理
    const index = viewList.findIndex((view) => view.name === newItem.name);
    if (index >= 0) {
      newFavoriteView = immutableDelete(newFavoriteView, ['viewList', index]);
    }
    const indexToInsert = newItem.isGlobal
      ? newFavoriteView.viewList.findIndex((item) => !item.isGlobal)
      : newFavoriteView.viewList.length;
    newFavoriteView = immutableInsert(newFavoriteView, ['viewList', indexToInsert], newItem);
    newFavoriteView = immutableSet(newFavoriteView, ['current'], newItem.name);
  } else if (type === 'update') {
    const { oldItem, newItem } = params;

    const index = viewList.findIndex((item) => item.name === oldItem.name);
    if (index >= 0) {
      newFavoriteView = immutableSet(newFavoriteView, ['viewList', index], newItem);

      // rename current, update
      if (current === oldItem.name) {
        newFavoriteView = immutableSet(newFavoriteView, ['current'], newItem.name);
      }
    }
  } else if (type === 'delete') {
    const { oldItem } = params;

    const index = viewList.findIndex((item) => item.name === oldItem.name);

    const newViewList = [...viewList];
    newViewList.splice(index, 1);
    const isCurrentDeleted = !newViewList.find((view) => view.name === current);
    newFavoriteView = {
      current: isCurrentDeleted ? null : current,
      viewList: newViewList,
    };
  } else if (type === 'switchCurrent') {
    const { name } = params;
    newFavoriteView.current = name || null;
  }

  await saveFavoriteView(key, globalKey, url, newFavoriteView, oldFavoriteView, type);

  return newFavoriteView;
}
